import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { ChevronUp, ChevronDown } from "lucide-react";
import { Button } from "@core/index";
import { VideoPlayer } from "../utils";

interface VideoSectionProps {
  isVideoVisible: boolean;
  isVideoExpanded: boolean;
  setIsVideoVisible: (visible: boolean) => void;
  setIsVideoExpanded: (expanded: boolean) => void;
  youtubeUrl?: string;
  generatorMode?: string;
}

export const VideoSection: React.FC<VideoSectionProps> = ({
  isVideoVisible,
  isVideoExpanded,
  setIsVideoVisible,
  setIsVideoExpanded,
  youtubeUrl,
  generatorMode,
}) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLIFrameElement>(null);

  if (generatorMode !== "YOUTUBE") {
    return null;
  }

  return (
    <div
      className={`transition-all duration-300 ${
        !isVideoVisible
          ? "w-12"
          : isVideoExpanded
            ? "w-full lg:w-2/3"
            : "w-full lg:w-96"
      } h-[300px] lg:h-full flex flex-col`}
    >
      <div className="flex items-center justify-between mb-2 gap-2">
        <Button
          onClick={() => setIsVideoVisible(!isVideoVisible)}
          textColor="text-gray-700 dark:text-gray-300 hover:text-rose-500 dark:hover:text-rose-400"
          className="flex items-center gap-2"
        >
          {isVideoVisible ? (
            <>
              <ChevronDown size={20} className="rotate-90" />
              <span className="hidden sm:inline">{t("common.hideVideo")}</span>
            </>
          ) : (
            <ChevronUp size={20} className="rotate-90" />
          )}
        </Button>
        {isVideoVisible && (
          <Button
            onClick={() => setIsVideoExpanded(!isVideoExpanded)}
            textColor="text-gray-700 dark:text-gray-300 hover:text-rose-500 dark:hover:text-rose-400"
            className="flex items-center gap-2"
          >
            {isVideoExpanded ? (
              <>
                <ChevronUp size={20} className="-rotate-90" />
                <span className="hidden sm:inline">{t("common.minimize")}</span>
              </>
            ) : (
              <>
                <ChevronDown size={20} className="-rotate-90" />
                <span className="hidden sm:inline">{t("common.expand")}</span>
              </>
            )}
          </Button>
        )}
      </div>
      {isVideoVisible && (
        <div className="relative flex-1 bg-gray-100 dark:bg-gray-700 rounded-lg overflow-hidden shadow-lg">
          {youtubeUrl && (
            <VideoPlayer
              ref={videoRef}
              url={youtubeUrl}
              className="absolute inset-0 w-full h-full"
            />
          )}
        </div>
      )}
    </div>
  );
};
